// Audit Trail service — append-only history of important user actions.
//
// Usage:
//   import { auditService, AUDIT_MODULES, AUDIT_ACTIONS, AUDIT_TAGS } from '../system/audit';
//   await auditService.log({
//     module: AUDIT_MODULES.TRIP,
//     action: AUDIT_ACTIONS.DELETE,
//     recordId: shipmentId,
//     recordLabel: shipment.trackingNumber,
//     before: oldData,
//     after: null,
//     tags: [AUDIT_TAGS.DELETIONS, AUDIT_TAGS.TRIP_CHANGES],
//     meta: { reason: 'user requested' }
//   });
//
// Actor (who) is read from the actor registry — set once after login by AuthContext.
// All entries are immutable: there is no update or delete API.

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit as fbLimit,
  serverTimestamp,
  db
} from '../../utils/database/firebase-config';

const COLLECTION = 'auditLogs';

export const AUDIT_MODULES = Object.freeze({
  TRIP: 'trip',
  CUSTOMER: 'customer',
  DRIVER: 'driver',
  EXTERNAL_DRIVER: 'external_driver',
  INVOICE: 'invoice',
  PAYMENT: 'payment',
  EXTERNAL_DRIVER_PAYMENT: 'external_driver_payment',
  EXTERNAL_DRIVER_LEDGER: 'external_driver_ledger',
  DOCUMENT: 'document',
  USER: 'user',
  ROLE: 'role',
  AUTH: 'auth',
  SETTINGS: 'settings'
});

export const AUDIT_ACTIONS = Object.freeze({
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  PRICE_CHANGE: 'price_change',
  COMMISSION_CHANGE: 'commission_change',
  STATUS_CHANGE: 'status_change',
  ROLE_CHANGE: 'role_change',
  PERMISSION_CHANGE: 'permission_change',
  GENERATE: 'generate',
  PAYMENT_RECORDED: 'payment_recorded',
  PAYMENT_DELETED: 'payment_deleted',
  LEDGER_ADJUSTMENT: 'ledger_adjustment',
  LOGIN: 'login',
  LOGOUT: 'logout'
});

export const AUDIT_TAGS = Object.freeze({
  ALL: 'all',
  FINANCIAL: 'financial',
  DELETIONS: 'deletions',
  TRIP_CHANGES: 'trip_changes',
  DRIVER_CHANGES: 'driver_changes',
  CUSTOMER_CHANGES: 'customer_changes',
  DOCUMENTS: 'documents',
  USER_ACCESS: 'user_access'
});

// ---------- Actor registry ----------
// Services don't have access to React hooks, so AuthContext registers the
// current actor here whenever the user changes. Audit log writes pull from this.
let _actor = null;

export const setAuditActor = (actor) => {
  // actor: { uid, email, name, role, companyId }
  _actor = actor || null;
};

export const getAuditActor = () => _actor;

// ---------- Diff helpers ----------

const isPlainObject = (v) => v && typeof v === 'object' && !Array.isArray(v);

/**
 * Compute a shallow field-level diff between two objects.
 * Returns { field: { from, to } } for fields that changed.
 * Used so audit entries don't need to store the entire record snapshot.
 */
export const computeDiff = (before, after, ignoreFields = ['updatedAt', 'createdAt']) => {
  const changes = {};
  const a = isPlainObject(before) ? before : {};
  const b = isPlainObject(after) ? after : {};
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    if (ignoreFields.includes(k)) continue;
    const av = a[k];
    const bv = b[k];
    if (JSON.stringify(av) !== JSON.stringify(bv)) {
      changes[k] = { from: av === undefined ? null : av, to: bv === undefined ? null : bv };
    }
  }
  return changes;
};

// Sanitize a value before storing — strip functions, Firestore sentinels, etc.
const sanitize = (v) => {
  if (v === undefined) return null;
  try {
    return JSON.parse(JSON.stringify(v));
  } catch {
    return null;
  }
};

// ---------- Core writer ----------

/**
 * Append an audit log entry. Never throws — audit failures must not break user actions.
 *
 * @param {Object} entry
 * @param {string} entry.module        One of AUDIT_MODULES
 * @param {string} entry.action        One of AUDIT_ACTIONS
 * @param {string} entry.recordId      ID of the affected record
 * @param {string} [entry.recordLabel] Human-readable label (trip number, invoice number, etc.)
 * @param {Object} [entry.before]      Snapshot before the change (or null)
 * @param {Object} [entry.after]       Snapshot after the change (or null)
 * @param {Array<string>} [entry.tags] Tags for filtering (deletions, financial, etc.)
 * @param {Object} [entry.meta]        Extra context (related ids, reason, etc.)
 * @param {string} [entry.companyId]   Override company id (defaults to actor's)
 */
const log = async (entry) => {
  try {
    const actor = _actor || {};
    const companyId = entry.companyId || actor.companyId || null;
    if (!companyId) {
      // Cannot scope this entry — drop it silently rather than mixing companies.
      console.warn('[audit] skipped log — no companyId available', entry);
      return null;
    }

    const before = sanitize(entry.before);
    const after = sanitize(entry.after);
    const changes = (entry.action === AUDIT_ACTIONS.UPDATE ||
                     entry.action === AUDIT_ACTIONS.PRICE_CHANGE ||
                     entry.action === AUDIT_ACTIONS.COMMISSION_CHANGE ||
                     entry.action === AUDIT_ACTIONS.ROLE_CHANGE ||
                     entry.action === AUDIT_ACTIONS.PERMISSION_CHANGE ||
                     entry.action === AUDIT_ACTIONS.STATUS_CHANGE)
      ? computeDiff(before, after)
      : null;

    const docData = {
      companyId,
      module: entry.module,
      action: entry.action,
      recordId: entry.recordId || null,
      recordLabel: entry.recordLabel || null,
      tags: Array.isArray(entry.tags) ? entry.tags : [],
      // who
      actorId: actor.uid || null,
      actorEmail: actor.email || null,
      actorName: actor.name || actor.email || null,
      actorRole: actor.role || null,
      // what
      before,
      after,
      changes,
      meta: sanitize(entry.meta) || null,
      // when
      timestamp: serverTimestamp(),
      createdAt: serverTimestamp()
    };

    const ref = await addDoc(collection(db, COLLECTION), docData);
    return ref.id;
  } catch (err) {
    // Audit must never break the underlying business action.
    console.error('[audit] failed to write entry', err);
    return null;
  }
};

/**
 * Convenience: log an update only if there are real changes between before/after.
 * Picks PRICE_CHANGE / COMMISSION_CHANGE / STATUS_CHANGE if those fields changed,
 * otherwise UPDATE. Adds appropriate tags automatically.
 */
const logUpdate = async ({ module, recordId, recordLabel, before, after, extraTags = [], meta }) => {
  const changes = computeDiff(before, after);
  if (Object.keys(changes).length === 0) return null;

  const priceFields = ['cost', 'price', 'finalTotal', 'total', 'subtotal', 'unitPrice'];
  const commissionFields = ['commission', 'commissionRate', 'driverCommission', 'commissionAmount'];
  const roleFields = ['role', 'permissions', 'modules'];
  const statusFields = ['status', 'paymentStatus'];

  let action = AUDIT_ACTIONS.UPDATE;
  const tags = new Set([AUDIT_TAGS.ALL, ...extraTags]);

  if (priceFields.some(f => f in changes)) {
    action = AUDIT_ACTIONS.PRICE_CHANGE;
    tags.add(AUDIT_TAGS.FINANCIAL);
  } else if (commissionFields.some(f => f in changes)) {
    action = AUDIT_ACTIONS.COMMISSION_CHANGE;
    tags.add(AUDIT_TAGS.FINANCIAL);
    tags.add(AUDIT_TAGS.DRIVER_CHANGES);
  } else if (roleFields.some(f => f in changes)) {
    action = AUDIT_ACTIONS.ROLE_CHANGE;
    tags.add(AUDIT_TAGS.USER_ACCESS);
  } else if (statusFields.some(f => f in changes)) {
    action = AUDIT_ACTIONS.STATUS_CHANGE;
  }

  return log({
    module, action, recordId, recordLabel,
    before, after,
    tags: Array.from(tags),
    meta
  });
};

// ---------- Read API (admin & per-record) ----------

const tsMs = (e) => {
  const t = e?.timestamp;
  if (!t) return 0;
  if (t.toDate) return t.toDate().getTime();
  if (t.seconds) return t.seconds * 1000;
  return new Date(t).getTime() || 0;
};

// Note: We deliberately avoid `orderBy('timestamp', 'desc')` in the query so
// Firestore doesn't require a composite index on (companyId, timestamp).
// Volumes are small enough to sort client-side; switch to an indexed query
// if audit traffic ever grows past a few thousand entries per company.
const fetchByCompany = async (companyId, max = 500) => {
  const q = query(
    collection(db, COLLECTION),
    where('companyId', '==', companyId),
    fbLimit(max)
  );
  const snap = await getDocs(q);
  const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  data.sort((a, b) => tsMs(b) - tsMs(a));
  return data;
};

/**
 * Apply client-side filters. Doing this in JS keeps Firestore index requirements low
 * and lets us combine arbitrary filters that Firestore can't compose.
 */
const applyFilters = (entries, filters = {}) => {
  const {
    quickFilter,         // 'all' | tag value
    dateFrom, dateTo,    // ISO date strings or Date
    actorId,
    module,
    action,
    customerId,
    driverId,
    tripNumber,
    invoiceNumber,
    paymentNumber,
    search
  } = filters;

  const fromTs = dateFrom ? new Date(dateFrom).getTime() : null;
  const toTs = dateTo ? new Date(dateTo).getTime() + 24 * 60 * 60 * 1000 - 1 : null;
  const searchLower = search ? String(search).toLowerCase() : null;

  return entries.filter((e) => {
    if (quickFilter && quickFilter !== AUDIT_TAGS.ALL) {
      if (!Array.isArray(e.tags) || !e.tags.includes(quickFilter)) return false;
    }
    if (module && e.module !== module) return false;
    if (action && e.action !== action) return false;
    if (actorId && e.actorId !== actorId) return false;

    const ts = e.timestamp?.toDate ? e.timestamp.toDate().getTime()
             : e.timestamp?.seconds ? e.timestamp.seconds * 1000
             : null;
    if (fromTs && ts && ts < fromTs) return false;
    if (toTs && ts && ts > toTs) return false;

    const meta = e.meta || {};
    if (customerId && meta.customerId !== customerId && e.recordId !== customerId) return false;
    if (driverId && meta.driverId !== driverId && e.recordId !== driverId) return false;
    if (tripNumber) {
      const haystack = `${meta.tripNumber || ''} ${meta.trackingNumber || ''} ${e.recordLabel || ''}`.toLowerCase();
      if (!haystack.includes(String(tripNumber).toLowerCase())) return false;
    }
    if (invoiceNumber) {
      const haystack = `${meta.invoiceNumber || ''} ${e.recordLabel || ''}`.toLowerCase();
      if (e.module !== AUDIT_MODULES.INVOICE && !haystack.includes(String(invoiceNumber).toLowerCase())) return false;
      if (e.module === AUDIT_MODULES.INVOICE && !haystack.includes(String(invoiceNumber).toLowerCase())) return false;
    }
    if (paymentNumber) {
      const haystack = `${meta.paymentNumber || ''} ${e.recordLabel || ''} ${e.recordId || ''}`.toLowerCase();
      if (!haystack.includes(String(paymentNumber).toLowerCase())) return false;
    }
    if (searchLower) {
      const blob = JSON.stringify(e).toLowerCase();
      if (!blob.includes(searchLower)) return false;
    }
    return true;
  });
};

/**
 * Fetch entries for a specific record (used by the Activity tab on detail pages).
 * Pulls all company entries and filters to recordId — fine for typical volumes.
 * If audit volume grows large, swap to a Firestore composite index on (companyId, recordId).
 */
// Module → which meta field carries this record's id when the entry's
// primary recordId is something else (e.g. payments and shipments reference
// a driver via meta.driverId). Lets a driver page surface payment add/delete
// and per-shipment commission changes alongside direct edits to the driver.
const RELATED_META_KEYS = {
  driver: ['driverId'],
  external_driver: ['driverId'],
  customer: ['customerId'],
  trip: ['tripId', 'shipmentId', 'tripNumber', 'trackingNumber'],
  invoice: ['invoiceId', 'invoiceNumber'],
  payment: ['paymentId', 'paymentNumber']
};

const getActivityForRecord = async (companyId, { module, recordId, max = 200 } = {}) => {
  if (!companyId || !recordId) return [];
  const all = await fetchByCompany(companyId, 1000);
  const relatedKeys = RELATED_META_KEYS[module] || [];
  return all
    .filter(e => {
      if (e.recordId === recordId) return true;
      const meta = e.meta || {};
      return relatedKeys.some(k => meta[k] === recordId);
    })
    .slice(0, max);
};

const getById = async (id) => {
  const ref = doc(db, COLLECTION, id);
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const auditService = {
  log,
  logUpdate,
  fetchByCompany,
  applyFilters,
  getActivityForRecord,
  getById,
  computeDiff,
  setActor: setAuditActor,
  getActor: getAuditActor
};

export default auditService;
